import { clearUserDetails } from '@/store/actions/auth';

/**
 * Freo Connect sends a customer back here when it stops accepting their token.
 *
 * A rejected customer is handed over to Connect with the access token this app
 * holds. That token is short lived, so a customer who leaves the tab open - or
 * comes back to it an hour later - finds every Connect call answering 401. From
 * there Connect signs them out and returns them to this app's login, flagging
 * the arrival with ?session_expired=true.
 *
 * The flag matters because without it the return trip is a loop: this app would
 * find its own stored ACCESS_DETAILS still marked authenticated, resume the
 * session, walk the customer back to the reject screen, and the skip-reject
 * guard would forward them to Connect again with the same dead token. Clearing
 * the stored session here breaks that - the customer gets the login screen,
 * which is what an expired session should produce.
 *
 * Kept in step with SESSION_EXPIRED_PARAM in freo-connect-ui's lib/session.ts.
 */
export const SESSION_EXPIRED_PARAM = 'session_expired';

/**
 * The router runs in history mode, so the flag belongs in the search - but the
 * Connect url is configured with a hash route on the end ("https://.../#/"),
 * and anything appending to that by hand lands the flag inside the hash
 * instead. Both are read so either shape works.
 */
function hasSessionExpiredFlag(): boolean {
  try {
    if (
      new URLSearchParams(window.location.search).get(SESSION_EXPIRED_PARAM) ===
      'true'
    ) {
      return true;
    }
    const hash = window.location.hash || '';
    const queryStart = hash.indexOf('?');
    if (queryStart === -1) {
      return false;
    }
    return (
      new URLSearchParams(hash.slice(queryStart)).get(SESSION_EXPIRED_PARAM) ===
      'true'
    );
  } catch (err) {
    return false;
  }
}

/**
 * Takes the flag back out of the address bar once it has been acted on, so a
 * refresh or a shared link does not read as another expiry and wipe a session
 * the customer has since signed back into.
 */
function stripSessionExpiredFlag(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(SESSION_EXPIRED_PARAM);
    const hash = url.hash || '';
    const queryStart = hash.indexOf('?');
    if (queryStart !== -1) {
      const hashParams = new URLSearchParams(hash.slice(queryStart));
      hashParams.delete(SESSION_EXPIRED_PARAM);
      const rest = hashParams.toString();
      url.hash = hash.slice(0, queryStart) + (rest ? `?${rest}` : '');
    }
    window.history.replaceState({}, document.title, url.toString());
  } catch (err) {
    console.log('Could not clean the session_expired flag off the url', err);
  }
}

/**
 * Call once, before the router mounts, so the auth guard sees the cleared
 * session rather than the stale one.
 */
export function handleSessionExpiredReturn(): void {
  if (!hasSessionExpiredFlag()) {
    return;
  }
  try {
    clearUserDetails();
  } catch (err) {
    console.log('Error clearing the expired session', err);
  }
  stripSessionExpiredFlag();
}
